import { useEffect, useMemo } from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { GiSettingsKnobs } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { PremiumIcon } from '../Navbar/Icons';

// eslint-disable-next-line import/order
import { Button, Card, Divider } from '@/components';

import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import { useTeamStore } from '@/hooks/useTeamStore';
import ROUTES from '@/router/routes';
import { EnumTeamContext } from '@/types/team';
import { cn, formatToBalanceArs } from '@/utils';
import 'react-tooltip/dist/react-tooltip.css';

export type TypesHeaderInfoTeam = {
  context?: EnumTeamContext;
  onClick?: () => void;
};

export const HeaderMyTeam = ({ context, onClick }: TypesHeaderInfoTeam) => {
  const { isMobile, isDesktop } = useIsDesktop();
  const { state } = useTeamStore();
  const { pinsData, handleSetPinsData } = usePinsAccount();
  const { userInitialData } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    handleSetPinsData({
      ...pinsData,
      pinsAvailable: pinsData.pinsAvailable,
    });
  }, []);

  const isFilterTeamVariant =
    context &&
    [EnumTeamContext.FilterTeam11, EnumTeamContext.FilterTeam5].includes(
      context
    );

  const displayBalance = useMemo(() => {
    if (
      context === EnumTeamContext.Team11 ||
      context === EnumTeamContext.Team5
    ) {
      return state.balance && `$${formatToBalanceArs(state.balance)}`;
    }

    if (
      state.isBeforeVeda &&
      userInitialData &&
      context === EnumTeamContext.FilterTeam11 &&
      state.balance
    ) {
      if (state.selectedPlayerOnTeam && state.selectedPlayerOnTeam?.cot) {
        return `$${formatToBalanceArs(
          userInitialData.presupuesto_total -
            state.balance +
            state.selectedPlayerOnTeam?.cot * 10000
        )}`;
      }
      return `$${formatToBalanceArs(
        userInitialData.presupuesto_total - state.balance
      )}`;
    }

    if (
      userInitialData &&
      context === EnumTeamContext.FilterTeam11 &&
      state.balance &&
      state.selectedPlayerOnTeam
    ) {
      return `$${formatToBalanceArs(
        userInitialData.presupuesto_total -
          state.balance +
          state.selectedPlayerOnTeam?.cot * 10000
      )}`;
    }

    if (context === EnumTeamContext.FilterTeam5) {
      return 'ilimitado';
    }
  }, [
    context,
    state.balance,
    state.isBeforeVeda,
    state.selectedPlayerOnTeam,
    userInitialData,
  ]);

  return (
    <>
      <Card
        className={cn(
          'card--rounded text-left flex w-full justify-between items-start font-semibold text-gdt-gray-4 text-3xs min-h-[76px]',
          {
            '!px-6 !py-2 !items-center !rounded-none': isFilterTeamVariant,
            'lg:items-center lg:text-gdt-gray-3 lg:text-base':
              !isFilterTeamVariant,
            'justify-normal': pinsData.isPremium && !isFilterTeamVariant,
          }
        )}
      >
        {!isFilterTeamVariant &&
          (context === EnumTeamContext.Team5 && isDesktop ? (
            <div className="lg:w-12 w-0" />
          ) : (
            <>
              <Button
                className="lg:block hidden lg:w-24 lg:text-base disabled"
                data-tooltip-html={`Las tácticas disponibles son: 4-4-2 | 4-3-3 | 3-4-3 | 4-5-1 | 3-5-2 | 5-3-2 | 3-3-4 | 4-2-4 | 5-2-3.<br>Para cambiar de táctica quitá y agregá futbolistas de las posiciones correspondientes.`}
                data-tooltip-id="tooltip-formacion"
                type="button"
                variant="secondary"
              >
                {state.tactic?.formation.join().replaceAll(',', '-')}
              </Button>

              <Tooltip
                className="max-w-[700px] whitespace-normal text-left"
                float={false} // evita que cambie automáticamente
                id="tooltip-formacion"
                place="right" // fuerza la posición a la derecha
              />
            </>
          ))}
        <div
          className={cn('flex gap-2', {
            'lg:gap-6': !isFilterTeamVariant,
            'lg:gap-11': context === EnumTeamContext.Team5,
            'lg:flex-1 lg:justify-center lg:max-w-[calc(100%-9rem)]':
              pinsData.isPremium,
          })}
        >
          <div
            className={cn(
              !isFilterTeamVariant && 'lg:flex lg:items-center lg:gap-3'
            )}
          >
            <p className="lg:mb-0 mb-[6px]">
              {isFilterTeamVariant ? 'DISPONIBLE' : 'PRESUPUESTO'}
            </p>
            <span
              className={cn('font-bold text-gdt-gray-2 text-lg ', {
                'lg:text-gdt-gray lg:text-xl': !isFilterTeamVariant,
              })}
            >
              {displayBalance}
            </span>
          </div>
          <Divider
            className={cn('h-[2.6rem]', {
              'lg:bg-gdt-gray-5 lg:h-[1.4rem]': !isFilterTeamVariant,
            })}
            type="vertical"
          />
          <div
            className={cn('w-[75px] refPins', {
              'lg:w-auto lg:flex lg:items-center': !isFilterTeamVariant,
              'w-auto': pinsData.isPremium,
            })}
          >
            <div className="flex items-center relative lg:gap-2 lg:mr-4">
              <p className="mb-[6px] lg:mb-0">MIS PINES</p>
              {!isFilterTeamVariant && (
                <FiInfo
                  className="text-gdt-blue relative lg:relative lg:right-auto left-[5px] lg:left-0 lg:top-0 -top-[3px] cursor-pointer hover:opacity-90"
                  // data-tooltip-id="tooltip-info"
                  size={18}
                  onClick={() => {
                    handleSetPinsData({
                      ...pinsData,
                      showInputsModalPins: false,
                      showModalWhatsPins: true,
                    });
                  }}
                />
              )}
            </div>
            <span
              className={cn('font-bold text-gdt-gray-2 text-lg', {
                'lg:text-xl lg:text-gdt-gray': !isFilterTeamVariant,
              })}
            >
              {!pinsData.isPremium ? (
                pinsData.pinsAvailable || 0
              ) : (
                <div className="flex items-center gap-1">
                  <figure className="scale-125">
                    <PremiumIcon context="page" variant="premium" />
                  </figure>
                  <span className="text-xs lg:text-sm text-gdt-status-yellow">
                    ILIMITADOS
                  </span>
                </div>
              )}
            </span>
          </div>
        </div>
        {/* <ReactTooltip
          content="INFO"
          id="tooltip-info"
          place="top"
          style={{
            backgroundColor: '#3366FF',
            fontWeight: 400,
            padding: '4px 8px',
            lineHeight: '1',
            fontSize: '.625rem',
            color: 'white',
          }}
        /> */}
        {isFilterTeamVariant ? (
          <Button
            className={cn('py-2 flex items-center gap-x-1 text-xs', {
              'bg-[#CCD9FF] border-gdt-blue': state.showFiltersPlayersList,
            })}
            type="button"
            variant={'secondary'}
            onClick={onClick}
          >
            {(!state.showFiltersPlayersList && isDesktop) || isMobile ? (
              <>
                <GiSettingsKnobs className="w-4 h-4 rotate-90" /> Filtros
              </>
            ) : (
              <>
                <FiX className="w-4 h-4 rotate-90" /> Cerrar
              </>
            )}
          </Button>
        ) : (
          !pinsData.isPremium && (
            <Button
              className="h-[2.25rem] lg:h-[2.6rem] lg:w-[8.4rem] px-2 py-0 text-xs lg:text-sm self-center"
              type="button"
              variant={isDesktop ? 'primary' : 'secondary'}
              onClick={() => navigate(ROUTES.myAccount)}
            >
              Obtener pines
            </Button>
          )
        )}
      </Card>
    </>
  );
};
